import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { LastQuoteChip } from './LastQuoteChip';

const rateForItem = (product) => {
  const costing = product.linked_costing || product.suggested_costing;
  if (!costing) return Number(product.target_price || 0);
  if (product.quantity_unit === 'kg') {
    if (costing.selling_price_per_kg) return Number(costing.selling_price_per_kg);
    const weight = Number(product.net_weight_grams || 0);
    return weight ? (Number(costing.selling_price_per_piece || 0) * 1000) / weight : 0;
  }
  return Number(costing.selling_price_per_piece || 0);
};

/** Read-only preview of the inquiry lines that will become the draft quotation. */
export const InquiryQuoteLinesPreview = ({ items }) => (
  <Card className="card-dashboard lg:col-span-2" data-testid="inquiry-quote-lines-preview">
    <CardHeader>
      <CardTitle className="text-lg font-heading uppercase">
        Lines from this inquiry ({items.length})
      </CardTitle>
    </CardHeader>
    <CardContent className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Part number</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Qty</TableHead>
            <TableHead>Costing basis</TableHead>
            <TableHead>Rate to quote (INR)</TableHead>
            <TableHead>Last quoted</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((product) => {
            const costing = product.linked_costing || product.suggested_costing;
            const rate = rateForItem(product);
            const unit = product.quantity_unit === 'kg' ? 'kg' : 'pc';
            return (
              <TableRow key={product.id} data-testid={`quote-preview-row-${product.id}`}>
                <TableCell className="font-mono font-medium">
                  {product.internal_part_no || product.customer_part_no || 'Unlinked'}
                </TableCell>
                <TableCell className="min-w-[200px]">{product.description}</TableCell>
                <TableCell className="font-mono">
                  {product.quantity == null ? (
                    <span className="flex items-center gap-1 text-amber-700">
                      <AlertTriangle className="h-3 w-3" /> Missing
                    </span>
                  ) : (
                    `${Number(product.quantity).toLocaleString('en-IN')} ${unit === 'kg' ? 'Kg' : 'Pcs'}`
                  )}
                </TableCell>
                <TableCell>
                  {costing ? (
                    <Badge
                      variant="outline"
                      className={product.costing_state === 'approved'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-amber-100 text-amber-900'}
                    >
                      {costing.costing_number}
                      {product.costing_state === 'approved' ? '' : ' · not approved'}
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">No costing — target price used</span>
                  )}
                </TableCell>
                <TableCell className="font-mono" data-testid={`quote-preview-rate-${product.id}`}>
                  {rate ? `₹${rate.toFixed(4)}/${unit}` : '—'}
                </TableCell>
                <TableCell>
                  <LastQuoteChip
                    lastQuote={product.last_quote}
                    newRate={rate}
                    testId={`quote-preview-last-quote-${product.id}`}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <p className="mt-3 text-xs text-muted-foreground">
        Rates come from each item's costing. You can edit every line in the next step before sending.
      </p>
    </CardContent>
  </Card>
);
